import React from 'react';
import { motion } from 'framer-motion';
import { X, Check, Loader2, ShoppingBag } from 'lucide-react';

export const ProductDetailModal = ({
  product,
  isOpen,
  onClose,
  onTryOn,
  onAddToCart,
  hasTryOnResult,
  isInCart,
  isLoading,
}) => {
  if (!isOpen || !product) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center"
      onClick={onClose}
    >
      <motion.div
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        className="bg-white w-full sm:w-[420px] sm:rounded-2xl rounded-t-2xl max-h-[90vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative aspect-[4/5] bg-slate-100">
          <img
            src={product.primary_image_url || product.images?.[0]?.image_url}
            alt={product.product_name || product.name}
            className="w-full h-full object-cover"
          />
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-10 h-10 bg-white/90 backdrop-blur rounded-full flex items-center justify-center shadow-lg"
          >
            <X className="w-5 h-5 text-slate-700" />
          </button>
          {hasTryOnResult && (
            <div className="absolute top-4 left-4 px-3 py-1.5 bg-green-500 text-white text-xs font-medium rounded-full flex items-center gap-1">
              <Check className="w-3.5 h-3.5" />
              Tried On
            </div>
          )}
        </div>

        <div className="p-5">
          <h3 className="text-lg font-semibold text-slate-800 mb-1">
            {product.product_name || product.name}
          </h3>
          <p className="text-2xl font-bold text-blue-600 mb-3">
            ₹{(product.selling_price || product.price || 0).toLocaleString()}
          </p>
          <div className="space-y-2 mb-4 text-sm">
            {product.sku_id && (
              <div className="flex justify-between">
                <span className="text-slate-500">SKU</span>
                <span className="text-slate-700">{product.sku_id}</span>
              </div>
            )}
            {product.category && (
              <div className="flex justify-between">
                <span className="text-slate-500">Category</span>
                <span className="text-slate-700">{product.category}</span>
              </div>
            )}
            {product.gender && (
              <div className="flex justify-between">
                <span className="text-slate-500">For</span>
                <span className="text-slate-700 capitalize">{product.gender}</span>
              </div>
            )}
            {product.description && (
              <p className="text-slate-600 mt-3 pt-3 border-t border-slate-100">{product.description}</p>
            )}
          </div>

          <div className="flex gap-3">
            <button
              onClick={onTryOn}
              disabled={isLoading}
              className="flex-1 py-3 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-colors flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <><Loader2 className="w-4 h-4 animate-spin" />Creating...</>
              ) : hasTryOnResult ? (
                'View Try-On'
              ) : (
                'Try it on Me'
              )}
            </button>
            {hasTryOnResult && (
              <button
                onClick={onAddToCart}
                disabled={isInCart}
                className={`py-3 px-4 rounded-xl font-medium transition-colors flex items-center gap-2 ${
                  isInCart ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                <ShoppingBag className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};
